import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

interface UseQueryParamOptions<T> {
  /** Default value to use when query param is not present */
  defaultValue: T;
  /**
   * Function to parse the string value from URL to your desired type
   * If not provided, will return the string as-is (typed as T)
   */
  parser?: (value: string) => T;
  /**
   * Function to serialize your value back to a string for the URL
   * If not provided, will use toString() method
   */
  serializer?: (value: T) => string;
  /** Whether to update the URL when the value changes (default: true) */
  updateURL?: boolean;
}

/**
 * Custom hook to manage a query parameter with automatic URL sync
 * @param paramName - The name of the query parameter
 * @param options - Configuration options
 * @returns [value, setValue, isReady] tuple where isReady indicates if the value has been initialized from the URL
 */
export function useQueryParam<T = string>(
  paramName: string,
  options: UseQueryParamOptions<T>,
): [T, (value: T) => void, boolean] {
  const router = useRouter();
  const {
    defaultValue,
    parser,
    serializer = (value: T) => String(value),
    updateURL = true,
  } = options;

  // Don't initialize with defaultValue - wait until we check the URL
  const [value, setValue] = useState<T | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize value from URL on mount - only run once when router is ready
  useEffect(() => {
    if (router.isReady && !isInitialized) {
      const queryValue = router.query[paramName] as string;

      if (queryValue !== undefined) {
        // Query param exists - use it
        try {
          const parsedValue = parser ? parser(queryValue) : (queryValue as T);
          setValue(parsedValue);
        } catch (error) {
          console.warn(`Failed to parse query param "${paramName}":`, error);
          setValue(defaultValue);
        }
      } else {
        // No query param found - use default
        setValue(defaultValue);
      }

      setIsInitialized(true);
    }
  }, [router.isReady, isInitialized]);

  // Sync value when the URL param is changed externally (e.g. nav search bar
  // shallow-pushes a new ?sport= without going through updateValue)
  const rawQueryValue = router.query[paramName] as string | undefined;
  useEffect(() => {
    if (!isInitialized || rawQueryValue === undefined) return;
    try {
      const parsedValue = parser ? parser(rawQueryValue) : (rawQueryValue as T);
      setValue(parsedValue);
    } catch {
      // ignore — external change with unparseable value, keep current
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawQueryValue, isInitialized]);

  // Update URL when value changes (only after initialization)
  const updateValue = useCallback(
    (newValue: T) => {
      setValue(newValue);

      if (updateURL && router.isReady && isInitialized) {
        const serializedValue = serializer(newValue);
        const currentQuery = { ...router.query };

        // Remove param if it matches default value, otherwise set it
        if (newValue === defaultValue) {
          delete currentQuery[paramName];
        } else {
          currentQuery[paramName] = serializedValue;
        }

        router.push(
          {
            pathname: router.pathname,
            query: currentQuery,
          },
          undefined,
          { shallow: true },
        );
      }
    },
    [router, paramName, serializer, defaultValue, updateURL, isInitialized],
  );

  return [value ?? defaultValue, updateValue, isInitialized];
}
